<template>
  <label :class="['switch', sizeClass, colorClass]">
    <input type="checkbox" :checked="isChecked" @change="updateValue" :disabled="disabled">
    <span class="slider"></span>
  </label>
</template>



<script setup>

import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    required: true
  },
  trueValue: {
    default: true
  },
  falseValue: {
    default: false
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  color: {
    type: String,
    default: 'primary',
    validator: (value) => ['default', 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'white', 'green', 'light-green', 'gray', 'orange'].includes(value)
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const isChecked = computed(() => props.modelValue === props.trueValue)
const sizeClass = computed(() => `switch-${props.size}`)
const colorClass = computed(() => `switch-${props.color}`)

function updateValue(event) {
  emit('update:modelValue', event.target.checked ? props.trueValue : props.falseValue)
}
</script>

<style scoped>
.switch {
  /* CSS Variables for ZSSwitch Component */
  /* Switch Base */
  --zs-switch-position: relative;
  --zs-switch-display: inline-block;
  --zs-switch-font-size: 8px;

  /* Switch Medium Size (Default) */
  --zs-switch-medium-width: 40px;
  --zs-switch-medium-height: 24px;
  --zs-switch-medium-thumb-size: 16px;
  --zs-switch-medium-thumb-gap: 4px;
  --zs-switch-medium-translate: 16px;

  /* Switch Small Size */
  --zs-switch-small-width: 32px;
  --zs-switch-small-height: 18px;
  --zs-switch-small-thumb-size: 12px;
  --zs-switch-small-thumb-gap: 3px;
  --zs-switch-small-translate: 14px;

  /* Switch Large Size */
  --zs-switch-large-width: 60px;
  --zs-switch-large-height: 34px;
  --zs-switch-large-thumb-size: 26px;
  --zs-switch-large-thumb-gap: 4px;
  --zs-switch-large-translate: 26px;

  /* Slider Style */
  --zs-switch-slider-bg-color: #ccc;
  /* var(--color-switch-slider-bg) */
  --zs-switch-slider-hover-bg-color: #b3b3b3;
  /* var(--color-switch-slider-hover-bg) */
  --zs-switch-slider-border-radius: 34px;
  --zs-switch-slider-transition: 0.4s;

  /* Thumb Style */
  --zs-switch-thumb-bg-color: white;
  /* var(--color-switch-thumb-bg) */
  --zs-switch-thumb-border-radius: 50%;

  /* Focus Style */
  --zs-switch-focus-shadow: 0 0 0 2px rgba(176, 229, 245, 0.3);
  /* var(--color-switch-focus-shadow) */

  /* Disabled Style */
  --zs-switch-disabled-opacity: 0.7;

  /* Button Colors (from colors.css) */
  --color-button-primary: #4384BD;
  --color-button-primary-hover: #3b77aa;
  --color-button-secondary: #6c757d;
  --color-button-secondary-hover: #5a6268;
  --color-button-success: #27AE60;
  --color-button-success-hover: #94bf3b;
  --color-button-danger: #EB7629;
  --color-button-danger-hover: #d66a25;
  --color-button-warning: #ffc107;
  --color-button-warning-hover: #e0a800;
  --color-button-info: #F9F9F9;
  --color-button-info-hover: #e5e5e5;
  --color-button-default: #D0E0EE;
  --color-button-default-hover: #c3d2df;
  --color-button-disabled: #E5E5E5;
  --color-button-light-success: #EAFAEB;
  --color-button-light-success-hover: #c8e6c9;

  /* Text Colors (from colors.css) */
  --color-text-primary: #FFFFFF;
  --color-text-secondary: #FFFFFF;
  --color-text-success: #FFFFFF;
  --color-text-danger: #FFFFFF;
  --color-text-warning: #212529;
  --color-text-info: #4384BD;
  --color-text-default: #4384BD;
  --color-text-disabled: #999999;
  --color-text-light-success: #27AE60;

  /* Base Colors */
  --color-blue: #4384BD;

  position: var(--zs-switch-position);
  display: var(--zs-switch-display);
  width: var(--zs-switch-medium-width);
  height: var(--zs-switch-medium-height);
  font-size: var(--zs-switch-font-size);
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--zs-switch-slider-bg-color);
  transition: var(--zs-switch-slider-transition);
  border-radius: var(--zs-switch-slider-border-radius);
}

.slider:before {
  position: absolute;
  content: "";
  height: var(--zs-switch-medium-thumb-size);
  width: var(--zs-switch-medium-thumb-size);
  left: var(--zs-switch-medium-thumb-gap);
  bottom: var(--zs-switch-medium-thumb-gap);
  background-color: var(--zs-switch-thumb-bg-color);
  transition: var(--zs-switch-slider-transition);
  border-radius: var(--zs-switch-thumb-border-radius);
}

/* Basic style - overridden by color classes */
input:checked+.slider {
  background-color: white;
}

input:checked+.slider:before {
  transform: translateX(var(--zs-switch-medium-translate));
}

/* Size variations */
/* Small size */
.switch-small {
  width: var(--zs-switch-small-width);
  height: var(--zs-switch-small-height);
}

.switch-small .slider:before {
  height: var(--zs-switch-small-thumb-size);
  width: var(--zs-switch-small-thumb-size);
  left: var(--zs-switch-small-thumb-gap);
  bottom: var(--zs-switch-small-thumb-gap);
}

.switch-small input:checked+.slider:before {
  transform: translateX(var(--zs-switch-small-translate));
}

/* Large size */
.switch-large {
  width: var(--zs-switch-large-width);
  height: var(--zs-switch-large-height);
}

.switch-large .slider:before {
  height: var(--zs-switch-large-thumb-size);
  width: var(--zs-switch-large-thumb-size);
  left: var(--zs-switch-large-thumb-gap);
  bottom: var(--zs-switch-large-thumb-gap);
}

.switch-large input:checked+.slider:before {
  transform: translateX(var(--zs-switch-large-translate));
}

/* Color variations */
/* Primary (Blue) */
.switch-primary input:checked+.slider {
  background-color: var(--color-button-primary);
}

.switch-primary input:checked+.slider:before {
  background-color: var(--color-text-primary);
}

.switch-primary input:hover:checked+.slider {
  background-color: var(--color-button-primary-hover);
}

/* Secondary */
.switch-secondary input:checked+.slider {
  background-color: var(--color-button-secondary);
}

.switch-secondary input:checked+.slider:before {
  background-color: var(--color-text-secondary);
}

.switch-secondary input:hover:checked+.slider {
  background-color: var(--color-button-secondary-hover);
}

/* Success/Green */
.switch-success input:checked+.slider,
.switch-green input:checked+.slider {
  background-color: var(--color-button-success);
}

.switch-success input:checked+.slider:before,
.switch-green input:checked+.slider:before {
  background-color: var(--color-text-success);
}

.switch-success input:hover:checked+.slider,
.switch-green input:hover:checked+.slider {
  background-color: var(--color-button-success-hover);
}

/* Light Green */
.switch-light-green input:checked+.slider {
  background-color: var(--color-button-light-success);
}

.switch-light-green input:checked+.slider:before {
  background-color: var(--color-text-light-success);
}

.switch-light-green input:hover:checked+.slider {
  background-color: var(--color-button-light-success-hover);
}

/* Danger/Orange */
.switch-danger input:checked+.slider,
.switch-orange input:checked+.slider {
  background-color: var(--color-button-danger);
}

.switch-danger input:checked+.slider:before,
.switch-orange input:checked+.slider:before {
  background-color: var(--color-text-danger);
}

.switch-danger input:hover:checked+.slider,
.switch-orange input:hover:checked+.slider {
  background-color: var(--color-button-danger-hover);
}

/* Warning/Yellow */
.switch-warning input:checked+.slider {
  background-color: var(--color-button-warning);
}

.switch-warning input:checked+.slider:before {
  background-color: var(--color-text-warning);
}

.switch-warning input:hover:checked+.slider {
  background-color: var(--color-button-warning-hover);
}

/* Info/Gray/Default */
.switch-info input:checked+.slider,
.switch-gray input:checked+.slider,
.switch-default input:checked+.slider {
  background-color: var(--color-button-default);
}

.switch-info input:checked+.slider:before,
.switch-gray input:checked+.slider:before,
.switch-default input:checked+.slider:before {
  background-color: var(--color-text-default);
}

.switch-info input:hover:checked+.slider,
.switch-gray input:hover:checked+.slider,
.switch-default input:hover:checked+.slider {
  background-color: var(--color-button-default-hover);
}

/* White */
.switch-white input:checked+.slider {
  background-color: #FFFFFF;
  border: 1px solid #CCCCCC;
}

.switch-white input:checked+.slider:before {
  background-color: var(--color-blue);
}

.switch-white input:hover:checked+.slider {
  background-color: #F8F8F8;
}

/* Disabled state */
.switch input:disabled+.slider {
  background-color: var(--color-button-disabled) !important;
  cursor: not-allowed;
  opacity: var(--zs-switch-disabled-opacity);
}

.switch input:disabled+.slider:before {
  background-color: var(--color-text-disabled) !important;
}

/* Hover effect for unchecked state */
.switch:not(.switch input:disabled) .slider:hover {
  background-color: var(--zs-switch-slider-hover-bg-color);
}

/* Focus style for better accessibility */
.switch input:focus+.slider {
  box-shadow: var(--zs-switch-focus-shadow);
}
</style>